import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Stack,
  HStack,
  Button,
  IconButton,
  useToast,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import Form from "@rjsf/chakra-ui";
import validator from "@rjsf/validator-ajv8";
import TypeaheadWidget from "../../../CommonComponents/TypeaheadWidget";
import ImageWidget from "../../../CommonComponents/ImageWidget";
import ImagePreview from "../../../components/ImagePreview";
import { getDeletedValues, removeKeysFromFormData } from "../../../util/helper";
import {
  getApiErrorMessage,
  showErrorToast,
  showSuccessToast,
} from "../../../util/errorHandler";
import { getResearchCategoriesAPI } from "../../../services/ResearchCategories";
import FormLoader from "../../../CommonComponents/FormLoader";
import {
  getInstitutionBasicData,
  updateInstitutionBasicData,
} from "../../../services/Institution";
import { fetchMasterlistSubTypes } from "../../../util/getMasterlistSubType";
import {
  homeCollageOrderSchema,
  homeCollageOrderUiSchema,
} from "../../../constants/homeCollage";
import { parseHomeCollageConflict } from "../../../util/homeCollageConflict";
import { useHomeCollageReassign } from "../../../hooks/useHomeCollageReassign";
import HomeCollageReassignDialog from "../../../CommonComponents/HomeCollageReassignDialog";
import {
  exploreCarouselOrderSchema,
  exploreCarouselOrderUiSchema,
} from "../../../constants/exploreCarousel";
import {
  exploreSearchHintOrderSchema,
  exploreSearchHintOrderUiSchema,
  exploreSearchHintKindSchema,
  exploreSearchHintKindUiSchema,
  useSearchHintPoolSize,
} from "../../../constants/exploreSearchHints"; // import SubmitButton from '../CommonComponents/SubmitButton';

/**
 * Trims each string in the array and removes empty/whitespace-only values.
 * Always returns an array so the API always receives a clean payload.
 * Scale note: O(n) — safe for any realistic AKA list size.
 * @param {Array<string>} arr
 * @returns {Array<string>}
 */
const cleanArray = (arr) =>
  (arr ?? []).map((v) => (v ?? "").trim()).filter((v) => v !== "");

const defaultValues = {
  aka: [],
  aka_typo_tolerance: [],
  keywords: [],
  has_scholarship: false,
  isExplore: false,
};

// Left Column Schema - Primary Fields
const getLeftColumnSchema = (hasIsExplore) => {
  const baseProperties = {
    alias: {
      type: "string",
      title: "Alias",
    },
    meta_title: {
      type: "string",
      title: "Meta Title",
    },
    meta_description: {
      type: "string",
      title: "Meta Description",
    },
    name: {
      type: ["string", "null"],
      title: "Institution Name",
    },
    first_name: {
      type: ["string", "null"],
      title: "Creator First Name",
    },
    last_name: {
      type: ["string", "null"],
      title: "Creator Surname",
    },
    full_name: {
      type: ["string", "null"],
      title: "Full Name",
    },
    image_alias: {
      type: ["string", "null"],
      title: "Image",
    },
    image_alias_preview: {
      type: ["string", "null"],
      title: "Image Preview",
    },
    subject: {
      type: "string",
      title: "Masterlist Type",
    },
    masterlist_type: {
      type: "string",
      title: "Masterlist Sub Type",
    },
    researchCategories: {
      type: "array",
      title: "Research Category",
      items: {
        type: "object",
        properties: {
          alias: {
            type: "string",
            title: "Alias",
          },
        },
      },
    },
  };

  // Add image URL field only if isExplore is true
  if (hasIsExplore) {
    baseProperties["image"] = {
      type: "string",
      title: "Explore Image URL",
    };
  }

  return {
    title: "",
    preface: "",
    type: "object",
    properties: baseProperties,
    required: ["alias", "subject", "masterlist_type"],
    default: defaultValues,
  };
};

// Right Column Schema - Secondary Fields
const getRightColumnSchema = (hasIsExplore) => {
  const baseProperties = {
    preface: {
      type: ["string", "null"],
      title: "Preface",
    },
    place_of_origin: {
      type: ["string", "null"],
      title: "Place of Origin",
    },
    country_of_origin: {
      type: ["string", "null"],
      title: "Country of Origin",
    },
    foundation_year: {
      type: ["string", "null"],
      title: "Foundation Year",
    },
    sortable_year_foundation: {
      type: ["string", "null"],
      title: "Sortable Year Foundation",
    },
    sortable_month_foundation: {
      type: ["string", "null"],
      title: "Sortable Month Foundation",
    },
    sortable_day_foundation: {
      type: ["string", "null"],
      title: "Sortable Day Foundation",
    },
    closure_year: {
      type: ["string", "null"],
      title: "Closure Year",
    },
    sortable_year_closure: {
      type: ["string", "null"],
      title: "Sortable Year Closure",
    },
    sortable_month_closure: {
      type: ["string", "null"],
      title: "Sortable Month Closure",
    },
    sortable_day_closure: {
      type: ["string", "null"],
      title: "Sortable Day Closure",
    },
    flourished_period: {
      type: ["string", "null"],
      title: "Flourished Period",
    },
    curriculum: {
      type: ["string", "null"],
      title: "Curriculum",
    },
    essential_reading: {
      type: ["string", "null"],
      title: "Essential Reading",
    },
    contemporary_antiquarian: {
      type: ["string", "null"],
      title: "Contemporary / Antiquarian",
    },
    visibility: {
      type: ["string", "null"],
      title: "Visibility",
    },
    institution: {
      type: ["string", "null"],
      title: "Institution",
    },
    year_of_establishment: {
      type: ["string", "null"],
      title: "Year of Establishment",
    },
    sortable_year: {
      type: ["string", "null"],
      title: "Sortable Year",
    },
    sortable_month: {
      type: ["string", "null"],
      title: "Sortable Month",
    },
    sortable_day: {
      type: ["string", "null"],
      title: "Sortable Day",
    },
    highlights: {
      type: ["string", "null"],
      title: "Highlights",
    },
    creator_image_alias: {
      type: "array",
      title: "Creator Image Alias",
      items: {
        type: "string",
      },
    },
    founder_image_alias: {
      type: "array",
      title: "Founder Image Alias",
      items: {
        type: "string",
      },
    },
    keywords_alias: {
      type: "array",
      title: "Keywords Alias",
      items: {
        type: "string",
      },
    },
    aka: {
      type: "array",
      title: "AKA",
      items: {
        type: "string",
      },
    },
    aka_typo_tolerance: {
      type: "array",
      title: "AKA Typo Tolerance",
      items: {
        type: "string",
      },
    },
    keywords: {
      type: "array",
      title: "Keywords",
      items: {
        type: "string",
      },
    },
    has_scholarship: {
      type: "boolean",
      title: "Has Scholarship",
      default: false,
    },
    isExplore: {
      type: "boolean",
      title: "Is Explore",
      default: false,
    },
  };

  // Add isExplore fields only if isExplore is true
  if (hasIsExplore) {
    baseProperties["display_name"] = {
      type: "string",
      title: "Display Name",
    };
    baseProperties["sort_name"] = {
      type: "string",
      title: "Sort Name",
    };
    baseProperties["route_type"] = {
      type: "string",
      title: "Route",
    };
    baseProperties["image"] = {
      type: "string",
      title: "Explore Image URL",
    };
    baseProperties["home_collage_order"] = homeCollageOrderSchema();
    baseProperties["explore_carousel_order"] = exploreCarouselOrderSchema();
    baseProperties["explore_search_hint_order"] =
      exploreSearchHintOrderSchema();
    baseProperties["explore_search_hint_kind"] = exploreSearchHintKindSchema();
  }

  const requiredFields = [];
  if (hasIsExplore) {
    requiredFields.push("route_type");
    requiredFields.push("image");
  }

  return {
    title: "",
    preface: "",
    type: "object",
    properties: baseProperties,
    required: requiredFields,
    default: defaultValues,
  };
};

// Left Column UI Schema
const getLeftColumnUiSchema = (hasIsExplore) => (isSubmitting) => {
  const baseSchema = {
    "ui:submitButtonOptions": {
      norender: true,
    },
    "ui:style": {
      fontSize: "14px",
    },
    researchCategories: {
      items: {
        alias: {
          "ui:widget": "typeahead1",
        },
      },
    },
    image_alias_preview: {
      "ui:widget": ImageWidget,
      "ui:options": {
        "ui:emptyValue": null,
      },
    },
    full_name: { "ui:emptyValue": "" },
    name: { "ui:emptyValue": "" },
    first_name: { "ui:emptyValue": "" },
    last_name: { "ui:emptyValue": "" },
    alias: { "ui:emptyValue": "", "ui:readonly": true },
    meta_title: { "ui:emptyValue": "" },
    meta_description: { "ui:widget": "textarea", "ui:emptyValue": "" },
    image_alias: { "ui:emptyValue": "" },
    subject: { "ui:emptyValue": "" },
    masterlist_type: { "ui:widget": "typeahead3", "ui:emptyValue": "" },
  };

  // Add image URL field only if isExplore is true
  if (hasIsExplore) {
    baseSchema.image = { "ui:widget": ImageWidget, "ui:emptyValue": "" };
  }

  return baseSchema;
};

// Right Column UI Schema
const getRightColumnUiSchema = (hasIsExplore) => (isSubmitting) => {
  const baseSchema = {
    "ui:submitButtonOptions": {
      norender: true,
    },
    "ui:style": {
      fontSize: "14px",
    },
    preface: { "ui:widget": "textarea", "ui:emptyValue": "" },
    place_of_origin: { "ui:emptyValue": "" },
    country_of_origin: { "ui:emptyValue": "" },
    foundation_year: { "ui:emptyValue": "" },
    sortable_year_foundation: { "ui:emptyValue": "" },
    sortable_month_foundation: { "ui:emptyValue": "" },
    sortable_day_foundation: { "ui:emptyValue": "" },
    closure_year: { "ui:emptyValue": "" },
    sortable_year_closure: { "ui:emptyValue": "" },
    sortable_month_closure: { "ui:emptyValue": "" },
    sortable_day_closure: { "ui:emptyValue": "" },
    flourished_period: { "ui:emptyValue": "" },
    curriculum: { "ui:emptyValue": "" },
    essential_reading: { "ui:emptyValue": "" },
    contemporary_antiquarian: { "ui:emptyValue": "" },
    visibility: { "ui:emptyValue": "" },
    institution: { "ui:emptyValue": "" },
    year_of_establishment: { "ui:emptyValue": "" },
    sortable_year: { "ui:emptyValue": "" },
    sortable_month: { "ui:emptyValue": "" },
    sortable_day: { "ui:emptyValue": "" },
    highlights: { "ui:widget": "textarea", "ui:emptyValue": "" },
    creator_image_alias: { "ui:emptyValue": [] },
    founder_image_alias: { "ui:emptyValue": [] },
    keywords_alias: { "ui:emptyValue": [] },
    keywords: { "ui:emptyValue": [] },
    isExplore: { "ui:emptyValue": false },
    has_scholarship: { "ui:emptyValue": false },
    aka_typo_tolerance: { "ui:emptyValue": [] },
    aka: { "ui:emptyValue": [] },
  };

  // Add isExplore fields only if isExplore is true
  if (hasIsExplore) {
    baseSchema.display_name = { "ui:emptyValue": "" };
    baseSchema.sort_name = { "ui:emptyValue": "" };
    baseSchema.route_type = { "ui:emptyValue": "" };
    baseSchema.image = { "ui:emptyValue": "" };
    baseSchema.home_collage_order = homeCollageOrderUiSchema();
    baseSchema.explore_carousel_order = exploreCarouselOrderUiSchema();
    baseSchema.explore_search_hint_order = exploreSearchHintOrderUiSchema();
    baseSchema.explore_search_hint_kind = exploreSearchHintKindUiSchema();
  }

  return baseSchema;
};

// Combined Schema for Single Column (Preview Mode)
const getCombinedSchema = (hasIsExplore) => {
  const baseProperties = {
    alias: {
      type: "string",
      title: "Alias",
    },
    meta_title: {
      type: "string",
      title: "Meta Title",
    },
    meta_description: {
      type: "string",
      title: "Meta Description",
    },
    name: {
      type: ["string", "null"],
      title: "Institution Name",
    },
    first_name: {
      type: ["string", "null"],
      title: "Creator First Name",
    },
    last_name: {
      type: ["string", "null"],
      title: "Creator Surname",
    },
    full_name: {
      type: ["string", "null"],
      title: "Full Name",
    },
    image_alias: {
      type: ["string", "null"],
      title: "Image",
    },
    image_alias_preview: {
      type: ["string", "null"],
      title: "Image Preview",
    },
    subject: {
      type: "string",
      title: "Masterlist Type",
    },
    masterlist_type: {
      type: "string",
      title: "Masterlist Sub Type",
    },
    researchCategories: {
      type: "array",
      title: "Research Category",
      items: {
        type: "object",
        properties: {
          alias: {
            type: "string",
            title: "Alias",
          },
        },
      },
    },
    preface: {
      type: ["string", "null"],
      title: "Preface",
    },
    place_of_origin: {
      type: ["string", "null"],
      title: "Place of Origin",
    },
    country_of_origin: {
      type: ["string", "null"],
      title: "Country of Origin",
    },
    foundation_year: {
      type: ["string", "null"],
      title: "Foundation Year",
    },
    sortable_year_foundation: {
      type: ["string", "null"],
      title: "Sortable Year Foundation",
    },
    sortable_month_foundation: {
      type: ["string", "null"],
      title: "Sortable Month Foundation",
    },
    sortable_day_foundation: {
      type: ["string", "null"],
      title: "Sortable Day Foundation",
    },
    closure_year: {
      type: ["string", "null"],
      title: "Closure Year",
    },
    sortable_year_closure: {
      type: ["string", "null"],
      title: "Sortable Year Closure",
    },
    sortable_month_closure: {
      type: ["string", "null"],
      title: "Sortable Month Closure",
    },
    sortable_day_closure: {
      type: ["string", "null"],
      title: "Sortable Day Closure",
    },
    flourished_period: {
      type: ["string", "null"],
      title: "Flourished Period",
    },
    curriculum: {
      type: ["string", "null"],
      title: "Curriculum",
    },
    essential_reading: {
      type: ["string", "null"],
      title: "Essential Reading",
    },
    contemporary_antiquarian: {
      type: ["string", "null"],
      title: "Contemporary / Antiquarian",
    },
    visibility: {
      type: ["string", "null"],
      title: "Visibility",
    },
    institution: {
      type: ["string", "null"],
      title: "Institution",
    },
    year_of_establishment: {
      type: ["string", "null"],
      title: "Year of Establishment",
    },
    sortable_year: {
      type: ["string", "null"],
      title: "Sortable Year",
    },
    sortable_month: {
      type: ["string", "null"],
      title: "Sortable Month",
    },
    sortable_day: {
      type: ["string", "null"],
      title: "Sortable Day",
    },
    highlights: {
      type: ["string", "null"],
      title: "Highlights",
    },
    creator_image_alias: {
      type: "array",
      title: "Creator Image Alias",
      items: {
        type: "string",
      },
    },
    founder_image_alias: {
      type: "array",
      title: "Founder Image Alias",
      items: {
        type: "string",
      },
    },
    keywords_alias: {
      type: "array",
      title: "Keywords Alias",
      items: {
        type: "string",
      },
    },
    isExplore: {
      type: "boolean",
      title: "Is Explore",
      default: false,
    },
    has_scholarship: {
      type: "boolean",
      title: "Has Scholarship",
      default: false,
    },
    aka: {
      type: "array",
      title: "AKA",
      items: {
        type: "string",
      },
    },
    aka_typo_tolerance: {
      type: "array",
      title: "AKA Typo Tolerance",
      items: {
        type: "string",
      },
    },
    keywords: {
      type: "array",
      title: "Keywords",
      items: {
        type: "string",
      },
    },
  };

  // Add isExplore fields only if isExplore is true
  if (hasIsExplore) {
    baseProperties["display_name"] = {
      type: "string",
      title: "Display Name",
    };
    baseProperties["sort_name"] = {
      type: "string",
      title: "Sort Name",
    };
    baseProperties["route_type"] = {
      type: "string",
      title: "Route",
    };
    baseProperties["image"] = {
      type: "string",
      title: "Explore Image URL",
    };
    baseProperties["home_collage_order"] = homeCollageOrderSchema();
    baseProperties["explore_carousel_order"] = exploreCarouselOrderSchema();
    baseProperties["explore_search_hint_order"] =
      exploreSearchHintOrderSchema();
    baseProperties["explore_search_hint_kind"] = exploreSearchHintKindSchema();
  }

  const requiredFields = ["alias", "subject", "masterlist_type"];
  if (hasIsExplore) {
    requiredFields.push("route_type");
  }

  return {
    title: "Primary fields",
    preface: "",
    type: "object",
    properties: baseProperties,
    required: requiredFields,
    default: defaultValues,
  };
};

// Combined UI Schema for Single Column (Preview Mode)
const getCombinedUiSchema = (hasIsExplore) => (isSubmitting) => {
  const baseSchema = {
    "ui:submitButtonOptions": {
      norender: true,
    },
    "ui:style": {
      fontSize: "14px",
    },
    researchCategories: {
      items: {
        alias: {
          "ui:widget": "typeahead1",
        },
      },
    },
    image_alias_preview: {
      "ui:widget": ImageWidget,
      "ui:options": {
        "ui:emptyValue": null,
      },
    },
    full_name: { "ui:emptyValue": "" },
    name: { "ui:emptyValue": "" },
    first_name: { "ui:emptyValue": "" },
    last_name: { "ui:emptyValue": "" },
    alias: { "ui:emptyValue": "", "ui:readonly": true },
    image_alias: { "ui:emptyValue": "" },
    subject: { "ui:emptyValue": "" },
    masterlist_type: { "ui:widget": "typeahead3", "ui:emptyValue": "" },
    meta_title: { "ui:emptyValue": "" },
    meta_description: { "ui:widget": "textarea", "ui:emptyValue": "" },
    preface: { "ui:widget": "textarea", "ui:emptyValue": "" },
    place_of_origin: { "ui:emptyValue": "" },
    country_of_origin: { "ui:emptyValue": "" },
    foundation_year: { "ui:emptyValue": "" },
    sortable_year_foundation: { "ui:emptyValue": "" },
    sortable_month_foundation: { "ui:emptyValue": "" },
    sortable_day_foundation: { "ui:emptyValue": "" },
    closure_year: { "ui:emptyValue": "" },
    sortable_year_closure: { "ui:emptyValue": "" },
    sortable_month_closure: { "ui:emptyValue": "" },
    sortable_day_closure: { "ui:emptyValue": "" },
    flourished_period: { "ui:emptyValue": "" },
    curriculum: { "ui:emptyValue": "" },
    essential_reading: { "ui:emptyValue": "" },
    contemporary_antiquarian: { "ui:emptyValue": "" },
    visibility: { "ui:emptyValue": "" },
    institution: { "ui:emptyValue": "" },
    year_of_establishment: { "ui:emptyValue": "" },
    sortable_year: { "ui:emptyValue": "" },
    sortable_month: { "ui:emptyValue": "" },
    sortable_day: { "ui:emptyValue": "" },
    highlights: { "ui:widget": "textarea", "ui:emptyValue": "" },
    creator_image_alias: { "ui:emptyValue": [] },
    founder_image_alias: { "ui:emptyValue": [] },
    keywords_alias: { "ui:emptyValue": [] },
    has_scholarship: { "ui:emptyValue": false },
    isExplore: { "ui:emptyValue": false },
    keywords: { "ui:emptyValue": [] },
    aka_typo_tolerance: { "ui:emptyValue": [] },
    aka: { "ui:emptyValue": [] },
  };

  // Add isExplore fields only if isExplore is true
  if (hasIsExplore) {
    baseSchema.display_name = { "ui:emptyValue": "" };
    baseSchema.sort_name = { "ui:emptyValue": "" };
    baseSchema.route_type = { "ui:emptyValue": "" };
    baseSchema.image = { "ui:widget": ImageWidget, "ui:emptyValue": "" };
    baseSchema.home_collage_order = homeCollageOrderUiSchema();
    baseSchema.explore_carousel_order = exploreCarouselOrderUiSchema();
    baseSchema.explore_search_hint_order = exploreSearchHintOrderUiSchema();
    baseSchema.explore_search_hint_kind = exploreSearchHintKindUiSchema();
  }

  return baseSchema;
};

const Basic = () => {
  const { alias } = useParams();
  const [leftFormData, setLeftFormData] = useState({});
  const [rightFormData, setRightFormData] = useState({});
  const [combinedFormData, setCombinedFormData] = useState({});
  // Original data snapshot used for computing deleted values on submit (e.g. aka, aka_typo_tolerance)
  const [basicFieldData, setBasicFieldData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [quickSave, setQuickSave] = useState(false);
  const homeCollageReassign = useHomeCollageReassign();
  const [showImagePreview, setShowImagePreview] = useState(false);
  const toast = useToast();
  // Re-renders once the live hint pool size (app_configs) is known.
  useSearchHintPoolSize();
  const previewEnabled = useSelector((state) => state.preview.isEnabled);
  const leftFormRef = useRef(null);
  const rightFormRef = useRef(null);
  const combinedFormRef = useRef(null);

  // Function for create options
  const transformOptions = (data) => {
    return data.map((itemArray) => {
      // Convert the array of key-value pairs into a single object
      const entity = itemArray.reduce((acc, { key, value }) => {
        acc[key] = value;
        return acc;
      }, {});

      // Use the `alias` field for label and value
      return {
        label: entity.alias,
        value: entity.alias,
      };
    });
  };

  const fetchCategories = async (inputValue) => {
    const result = await getResearchCategoriesAPI();
    const transformedOptions = transformOptions(result);

    // This function return the options
    return transformedOptions.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase()),
    );
  };

  const transFormData = (data) => ({
    ...data,
    meta_title: data?.meta_title ?? "",
    meta_description: data?.meta_description ?? "",
    name: data?.name ?? "",
    first_name: data?.first_name ?? "",
    last_name: data?.last_name ?? "",
    full_name: data?.full_name ?? "",
    image_alias_preview: data?.image_alias || null,
    has_scholarship: data?.has_scholarship ?? false,
    isExplore: data?.isExplore ?? false,
    aka: Array.isArray(data?.aka) ? data.aka : [],
    aka_typo_tolerance: Array.isArray(data?.aka_type_tolerance)
      ? data.aka_type_tolerance
      : [],
    keywords: Array.isArray(data?.keywords) ? data.keywords : [],
    keywords_alias: Array.isArray(data?.keywords_alias)
      ? data.keywords_alias
      : [],
    creator_image_alias: Array.isArray(data?.creator_image_alias)
      ? data.creator_image_alias
      : [],
    founder_image_alias: Array.isArray(data?.founder_image_alias)
      ? data.founder_image_alias
      : [],
    researchCategories: Array.isArray(data?.researchCategories)
      ? data.researchCategories
      : [],
    preface: data?.preface ?? "",
    place_of_origin: data?.place_of_origin ?? "",
    country_of_origin: data?.country_of_origin ?? "",
    foundation_year: data?.foundation_year ?? "",
    sortable_year_foundation: data?.sortable_year_foundation ?? "",
    sortable_month_foundation: data?.sortable_month_foundation ?? "",
    sortable_day_foundation: data?.sortable_day_foundation ?? "",
    closure_year: data?.closure_year ?? "",
    sortable_year_closure: data?.sortable_year_closure ?? "",
    sortable_month_closure: data?.sortable_month_closure ?? "",
    sortable_day_closure: data?.sortable_day_closure ?? "",
    flourished_period: data?.flourished_period ?? "",
    curriculum: data?.curriculum ?? "",
    essential_reading: data?.essential_reading ?? "",
    contemporary_antiquarian: data?.contemporary_antiquarian ?? "",
    visibility: data?.visibility ?? "",
    institution: data?.institution ?? "",
    year_of_establishment: data?.year_of_establishment ?? "",
    sortable_year: data?.sortable_year ?? "",
    sortable_month: data?.sortable_month ?? "",
    sortable_day: data?.sortable_day ?? "",
    highlights: data?.highlights ?? "",
  });

  const widgets = {
    typeahead1: (props) => (
      <TypeaheadWidget {...props} fetchOptions={fetchCategories} />
    ),
    typeahead3: (props) => (
      <TypeaheadWidget {...props} fetchOptions={fetchMasterlistSubTypes} />
    ),
  };

  // Split data for two-column layout
  const splitFormData = (data = {}) => {
    const leftFields = [
      "alias",
      "meta_title",
      "meta_description",
      "name",
      "first_name",
      "last_name",
      "full_name",
      "image_alias",
      "image_alias_preview",
      "subject",
      "masterlist_type",
      "researchCategories",
    ];
    const rightFields = [
      "preface",
      "place_of_origin",
      "country_of_origin",
      "foundation_year",
      "sortable_year_foundation",
      "sortable_month_foundation",
      "sortable_day_foundation",
      "closure_year",
      "sortable_year_closure",
      "sortable_month_closure",
      "sortable_day_closure",
      "flourished_period",
      "curriculum",
      "essential_reading",
      "contemporary_antiquarian",
      "visibility",
      "institution",
      "year_of_establishment",
      "sortable_year",
      "sortable_month",
      "sortable_day",
      "highlights",
      "creator_image_alias",
      "founder_image_alias",
      "keywords_alias",
      "aka",
      "aka_typo_tolerance",
      "keywords",
      "has_scholarship",
      "display_name",
      "sort_name",
      "route_type",
      "isExplore",
      "image",
      "home_collage_order",
      "explore_carousel_order",
      "explore_search_hint_order",
      "explore_search_hint_kind",
    ];

    const leftData = {};
    const rightData = {};

    // Process left fields with defaults
    leftFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        leftData[field] = data[field];
      } else {
        if (field === "researchCategories") leftData[field] = [];
        else if (field === "image_alias_preview") leftData[field] = null;
        else leftData[field] = "";
      }
    });

    // Process right fields with defaults
    const arrayFieldNames = new Set([
      "creator_image_alias",
      "founder_image_alias",
    ]);
    const isArrayField = (field) =>
      field.includes("aka") ||
      field.includes("keywords") ||
      arrayFieldNames.has(field);
    rightFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        if (isArrayField(field)) {
          rightData[field] = Array.isArray(data[field]) ? data[field] : [];
        } else {
          rightData[field] = data[field];
        }
      } else {
        if (field === "has_scholarship" || field === "isExplore") {
          rightData[field] = false;
        } else if (isArrayField(field)) {
          rightData[field] = [];
        } else if (
          field === "home_collage_order" ||
          field === "explore_carousel_order" ||
          field === "explore_search_hint_order"
        ) {
          rightData[field] = null;
        } else {
          rightData[field] = "";
        }
      }
    });

    return { leftData, rightData };
  };

  // Combine data from both forms
  const combineFormData = (leftData, rightData) => {
    return { ...leftData, ...rightData };
  };

  // Check if isExplore is true (not just if it exists)
  const hasIsExplore = useMemo(() => {
    const dataToCheck = previewEnabled ? combinedFormData : rightFormData;
    return dataToCheck?.isExplore === true;
  }, [combinedFormData, rightFormData, previewEnabled]);

  const leftColumnSchema = useMemo(
    () => getLeftColumnSchema(hasIsExplore),
    [hasIsExplore],
  );
  const rightColumnSchema = useMemo(
    () => getRightColumnSchema(hasIsExplore),
    [hasIsExplore],
  );
  const combinedSchema = useMemo(
    () => getCombinedSchema(hasIsExplore),
    [hasIsExplore],
  );
  const leftColumnUiSchema = useMemo(
    () => getLeftColumnUiSchema(hasIsExplore),
    [hasIsExplore],
  );
  const rightColumnUiSchema = useMemo(
    () => getRightColumnUiSchema(hasIsExplore),
    [hasIsExplore],
  );
  const combinedUiSchema = useMemo(
    () => getCombinedUiSchema(hasIsExplore),
    [hasIsExplore],
  );

  // Fetch initial data
  useEffect(() => {
    const getBasicData = async () => {
      try {
        if (alias) {
          const result = await getInstitutionBasicData(alias);
          if (result) {
            const transformedData = transFormData(result);

            // Split data for two-column layout
            const { leftData, rightData } = splitFormData(transformedData);
            setLeftFormData(leftData);
            setRightFormData(rightData);
            setCombinedFormData(transformedData);
            setBasicFieldData(result);
            setQuickSave(false);
          }
        } else {
          // For new records, initialize empty form data
          const emptyData = {};
          const { leftData, rightData } = splitFormData(emptyData);
          setLeftFormData(leftData);
          setRightFormData(rightData);
          setCombinedFormData(emptyData);
        }
      } catch (error) {
        console.error("Error fetching film data:", error);
        showErrorToast(toast, "Failed to fetch film data");
      } finally {
        setIsLoading(false);
      }
    };

    getBasicData();
  }, [alias, toast, quickSave]);

  // Handle form changes for two-column layout
  const handleLeftFormChange = (data) => {
    setLeftFormData(data.formData);
  };

  const handleRightFormChange = (data) => {
    setRightFormData(data.formData);
  };

  const handleCombinedFormChange = (data) => {
    setCombinedFormData(data.formData);
  };

  // Submit updated data
  const handleSubmit = async () => {
    // Use combined form data in preview mode, otherwise combine left and right forms
    const finalFormData = previewEnabled
      ? combinedFormData
      : combineFormData(leftFormData, rightFormData);

    // Validate required fields before submission
    // RJSF validation should catch these, but this is an additional safeguard
    const requiredFields = {
      subject: finalFormData.subject, // Masterlist Type
      masterlist_type: finalFormData.masterlist_type, // Masterlist Sub Type
    };

    // Only validate route_type if isExplore is true
    if (hasIsExplore) {
      requiredFields.route_type = finalFormData.route_type;
    }

    // Check if any required field is empty or whitespace-only
    for (const fieldValue of Object.values(requiredFields)) {
      if (
        !fieldValue ||
        (typeof fieldValue === "string" && fieldValue.trim() === "")
      ) {
        // Don't submit if any required field is missing
        // RJSF validation will show the error message
        return;
      }
    }

    // Clean AKA arrays before submission:
    // - Trim leading/trailing whitespace from each entry
    // - Remove empty/whitespace-only values
    // - Always send an array (even if empty) — never send empty strings (PRD §6)
    const cleanedFormData = {
      ...finalFormData,
      aka: cleanArray(finalFormData.aka),
      // Map form-state key back to API key before submission
      aka_type_tolerance: cleanArray(finalFormData.aka_typo_tolerance),
      creator_image_alias: cleanArray(finalFormData.creator_image_alias),
      founder_image_alias: cleanArray(finalFormData.founder_image_alias),
      keywords_alias: cleanArray(finalFormData.keywords_alias),
    };

    // Remove UI-only and form-state-only keys before sending to API
    const keysToRemove = ["image_alias_preview", "aka_typo_tolerance"];

    // Remove extra fields added which are not available in DB
    const updatedFormData = removeKeysFromFormData(
      cleanedFormData,
      keysToRemove,
    );
    const deletedValues = getDeletedValues(basicFieldData, updatedFormData);

    // Shared by the initial attempt and the confirm-reassign retry below --
    // extraPayload merges in confirm_home_collage_order_reassign: true only
    // when the admin has explicitly confirmed taking a taken slot (#266343).
    const submitInstitution = (extraPayload = {}) =>
      updateInstitutionBasicData(
        alias,
        { ...updatedFormData, ...extraPayload },
        deletedValues,
      );

    try {
      if (alias) {
        setIsSubmitting(true);
        const result = await submitInstitution();

        if (result?.success) {
          setQuickSave((prev) => !prev);

          showSuccessToast(toast, "Data updated successfully");
        }
      }
    } catch (error) {
      const { conflict } = parseHomeCollageConflict(error);
      if (conflict) {
        // Let the admin confirm taking the slot instead of a dead-end error.
        // Confirming calls this same retry, with the flag set, so the slot
        // swap happens atomically on the backend in one call (#266343).
        homeCollageReassign.open(conflict, async () => {
          try {
            const result = await submitInstitution({
              confirm_home_collage_order_reassign: true,
            });
            if (result?.success) {
              setQuickSave((prev) => !prev);
              showSuccessToast(toast, "Data updated successfully");
            }
          } catch (retryError) {
            showErrorToast(
              toast,
              getApiErrorMessage(
                retryError,
                "Failed to update institution data",
              ),
            );
            throw retryError;
          }
        });
      } else {
        console.error("Error updating institution data:", error);
        showErrorToast(
          toast,
          getApiErrorMessage(error, "Failed to update institution data"),
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="auto" pb="100px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Box>
          {previewEnabled ? (
            /* Single column layout for preview mode */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Box
                flex={1}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                p={4}
                borderRadius="md"
              >
                <HStack justifyContent="space-between" mb={4}>
                  <Box fontSize="lg" fontWeight="bold">
                    Primary fields
                  </Box>
                  {combinedFormData?.image_alias_preview && (
                    <IconButton
                      aria-label={
                        showImagePreview
                          ? "Hide image preview"
                          : "Show image preview"
                      }
                      icon={showImagePreview ? <ViewOffIcon /> : <ViewIcon />}
                      size="sm"
                      colorScheme={showImagePreview ? "red" : "blue"}
                      onClick={() => setShowImagePreview((prev) => !prev)}
                      title={
                        showImagePreview
                          ? "Close image preview"
                          : "Open image preview"
                      }
                    />
                  )}
                </HStack>
                <Form
                  ref={combinedFormRef}
                  schema={combinedSchema}
                  uiSchema={combinedUiSchema(isSubmitting)}
                  formData={combinedFormData}
                  validator={validator}
                  onChange={handleCombinedFormChange}
                  onSubmit={(e) => {
                    // RJSF onSubmit receives { formData, errors } object, not a DOM event
                    // Only proceed if there are no errors (validation passed)
                    if (!e.errors || e.errors.length === 0) {
                      handleSubmit();
                    }
                  }}
                  widgets={widgets}
                  fontsize={"14px"}
                />
              </Box>
              {/* Third column for image preview when clicked */}
              {showImagePreview && combinedFormData?.image_alias_preview && (
                <HStack
                  sx={{ position: "sticky", top: "10px", marginLeft: "0px" }}
                >
                  <ImagePreview image={combinedFormData?.image_alias_preview} />
                </HStack>
              )}
            </Box>
          ) : (
            /* Two column layout with separate forms */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Stack
                flexDirection="row"
                gap={4}
                alignItems="flex-start"
                flex={1}
              >
                <HStack alignItems="flex-start" flex={1}>
                  {/* Left Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <HStack justifyContent="space-between" mb={4}>
                      <Box fontSize="lg" fontWeight="bold">
                        Primary fields
                      </Box>
                      {leftFormData?.image_alias_preview && (
                        <IconButton
                          aria-label={
                            showImagePreview
                              ? "Hide image preview"
                              : "Show image preview"
                          }
                          icon={
                            showImagePreview ? <ViewOffIcon /> : <ViewIcon />
                          }
                          size="sm"
                          colorScheme={showImagePreview ? "red" : "blue"}
                          onClick={() => setShowImagePreview((prev) => !prev)}
                          title={
                            showImagePreview
                              ? "Close image preview"
                              : "Open image preview"
                          }
                        />
                      )}
                    </HStack>
                    <Form
                      ref={leftFormRef}
                      schema={leftColumnSchema}
                      uiSchema={leftColumnUiSchema(isSubmitting)}
                      formData={leftFormData}
                      validator={validator}
                      onChange={handleLeftFormChange}
                      onSubmit={() => {
                        // Don't submit from left form - only right form triggers submission
                        // RJSF onSubmit receives { formData, errors }, not a DOM event
                      }}
                      widgets={widgets}
                      fontsize={"14px"}
                    />
                  </Box>
                  {/* Right Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <Form
                      ref={rightFormRef}
                      schema={rightColumnSchema}
                      uiSchema={rightColumnUiSchema(isSubmitting)}
                      formData={rightFormData}
                      validator={validator}
                      onChange={handleRightFormChange}
                      onSubmit={(e) => {
                        // RJSF onSubmit receives { formData, errors } object, not a DOM event
                        // Only proceed if there are no errors (validation passed)
                        if (!e.errors || e.errors.length === 0) {
                          handleSubmit();
                        }
                      }}
                      widgets={widgets}
                      fontsize={"14px"}
                    />
                  </Box>
                </HStack>
              </Stack>
              {/* Third column for image preview when clicked */}
              {showImagePreview && leftFormData?.image_alias_preview && (
                <HStack
                  sx={{ position: "sticky", top: "10px", marginLeft: "0px" }}
                >
                  <ImagePreview image={leftFormData?.image_alias_preview} />
                </HStack>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Fixed Save Button at Bottom */}
      <Box
        position="fixed"
        bottom={0}
        left="250px" // Account for sidebar width
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        p={4}
        zIndex={1000}
        display="flex"
      >
        <Box maxW="auto" display="flex" mx="auto">
          <Button
            colorScheme="blue"
            size="lg"
            width="auto"
            isLoading={isSubmitting}
            loadingText="Saving..."
            onClick={() => {
              // Trigger form validation by programmatically submitting all forms
              // This will trigger RJSF validation and show errors for required fields
              // (full_name, subject, masterlist_type, route_type)
              const forms = document.querySelectorAll("form");

              if (forms.length > 0) {
                // In preview mode: validate the combined form (has all required fields)
                // In two-column mode: validate both left form (full_name, subject, masterlist_type)
                // and right form (route_type)
                forms.forEach((form) => {
                  // Create a temporary submit button to trigger validation
                  const tempSubmitBtn = document.createElement("button");
                  tempSubmitBtn.type = "submit";
                  tempSubmitBtn.style.display = "none";
                  form.appendChild(tempSubmitBtn);

                  // Trigger form submit which will validate and show errors
                  // The onSubmit handlers will call handleSubmit() only if validation passes
                  tempSubmitBtn.click();
                  form.removeChild(tempSubmitBtn);
                });
              } else {
                // Fallback: call handleSubmit directly if no forms found
                handleSubmit();
              }
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>

      {/* Add padding to prevent content from being hidden behind fixed button */}
      <Box pb="100px" />

      <HomeCollageReassignDialog
        isOpen={homeCollageReassign.isOpen}
        conflict={homeCollageReassign.conflict}
        isConfirming={homeCollageReassign.isConfirming}
        cancelRef={homeCollageReassign.cancelRef}
        onConfirm={homeCollageReassign.confirm}
        onCancel={homeCollageReassign.close}
      />
    </Box>
  );
};

export default Basic;
